/**
 * Shared classification of transient browser network-layer fetch failures.
 *
 * A `fetch()` that rejects at the network layer — a long-idle Safari tab
 * resuming and aborting its in-flight requests, the user navigating away
 * mid-request, a dropped connection — produces a bare `TypeError` whose
 * message varies by browser. These are NOT logic defects (a backend 5xx
 * *resolves* with `response.ok === false`; it never rejects), so callers
 * treat them as expected, retry-able noise rather than real errors.
 *
 * Two consumers share this source of truth so the "what counts as a network
 * blip" list can't drift between them:
 *  - `rxdb/utils/lifecycle-hooks.ts` — to log a swallowed background-reconcile
 *    failure at `debug` (transient) vs `warn` (genuine, keep prod visibility).
 *  - `lib/posthog-exception-filter.ts` — to drop the residual framework-internal
 *    aborted-navigation `$exception` events from Error Tracking.
 */

/**
 * Lowercased substrings of browser-generated network-failure `TypeError`
 * messages across Safari / Chrome / Firefox. Matched case-insensitively via
 * `includes` so minor browser-version wording drift still classifies.
 */
export const NETWORK_FAILURE_MESSAGE_SUBSTRINGS = [
  "load failed", // Safari: fetch() network-layer rejection / aborted in-flight request
  "failed to fetch", // Chromium: equivalent network-layer rejection
  "networkerror when attempting to fetch resource", // Firefox
  "the network connection was lost", // Safari variant
  "the request timed out", // Safari timeout variant
] as const;

/**
 * True when an unknown thrown value looks like a transient browser network
 * abort: an explicit `AbortError`, or a message matching a known browser
 * network-failure phrase. Defensive against non-Error throws.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof Error && error.name === "AbortError") return true;
  const message = (
    error instanceof Error ? error.message : String(error)
  ).toLowerCase();
  return NETWORK_FAILURE_MESSAGE_SUBSTRINGS.some((m) => message.includes(m));
}
